/**
 * `AW48` · **Las cinco direcciones de Configuración del chatbot.** `R1.1 · F3`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐ LAS PESTAÑAS TIENEN DIRECCIÓN PROPIA, Y ACÁ ESTÁ CUÁL ES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `REQ-R1.1-F3-CA-13` (PRD:521) nombra las cinco y las ordena; `CA-14`…`CA-18`
 * (PRD:522-526) le dan a cada una su dirección. El orden de `PESTANAS` **es** el
 * de la barra: no es una lista de valores válidos, es la lista ordenada.
 *
 * | pestaña | dirección |
 * |---|---|
 * | Personalidad | `#/instancia/configuracion-del-chatbot/personalidad` |
 * | Guardrails | `#/instancia/configuracion-del-chatbot/guardrails` |
 * | Mensajes del motor | `#/instancia/configuracion-del-chatbot/mensajes` |
 * | Derivación | `#/instancia/configuracion-del-chatbot/derivacion` |
 * | Auditoría | `#/instancia/configuracion-del-chatbot/auditoria` |
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ LO QUE ESTE ARCHIVO **NO** PUEDE HACER, Y NO ES UN OLVIDO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Este módulo **lee y escribe la barra de direcciones**; no declara rutas. Quien
 * declara rutas es el marco (`src/App.tsx`, territorio de la stage del marco) y
 * esta stage **no toca la raíz de `src/`**: doce directorios importan de ahí.
 *
 * ⛔ La consecuencia, que es un REQUISITO CRUZADO y no un detalle: el marco tiene
 * que servir `instancia/configuracion-del-chatbot/*`, no sólo el prefijo exacto.
 * Con la ruta de hoy —coincidencia EXACTA— las cinco direcciones de arriba **no
 * las sirve nadie**, y esta pantalla quedaría sin montar en cuatro de las cinco.
 * Ningún examen de esta stage puede ver eso: se ve recién al navegar.
 *
 * ⛔ Y **la canonicalización de una dirección desconocida NO vive acá.** `CA-83`
 * (`#/…/xyz` tiene que *quedar* en `personalidad`) y `CA-84` (`#/…/mensajes/xyz`
 * tiene que *quedar* en la lista) dicen **quedan**, o sea: hay que reescribir la
 * barra. Reescribirla desde acá con `history.replaceState` desincroniza al
 * `HashRouter` —que escucha `popstate`, y `replaceState` no lo dispara— y deja la
 * barra diciendo una cosa y el router renderizando otra. ⇒ acá se **resuelve**
 * (una dirección desconocida se LEE como Personalidad, una lista desconocida se
 * LEE como la lista) y **reescribir la barra es del marco**.
 */

/**
 * Las cinco pestañas, **en el orden de la barra**. `CA-13`.
 *
 * ⚠️ El valor de cada una **es** la dirección: el segmento de la URL y la clave de
 * las tablas de rótulos son la misma cosa, así que no hay dos listas que puedan
 * divergir.
 */
export const PESTANAS = [
  "personalidad",
  "guardrails",
  "mensajes",
  "derivacion",
  "auditoria",
] as const

export type Pestana = (typeof PESTANAS)[number]

/**
 * ⭐ **Los siete mensajes del motor que tienen dirección propia**, en el orden de
 * la tabla. `PRD_R1.1.md:320` y `R1.1-F3` §Descripción funcional
 * («`fuera-de-tema`, `no-puedo-responder-eso`, `sin-cobertura-en-el-banco`,
 * `respuesta-de-respaldo`, `dame-un-segundo`, `pedir-que-escriba`»).
 *
 * ⭐⭐ **RASTRO · `R2-UI9` · 23/9/2026 — EL SÉPTIMO.** `ADR-0082`: el renglón de
 * arriba decía «los SEIS» y se conserva con su fuente, porque `R1.1-F3` sigue
 * nombrando esos seis y ninguno se movió. Lo que cambió es que `PRD_R2.md:1194–1203`
 * le suma **una séptima fila a Mensajes del motor**: «Botón sin respuesta», con
 * dirección propia `boton-sin-respuesta` (`R2-F3-CA-66`, `PRD_R2.md:1340`).
 *
 * ⚠️ **El slug lo fija el CA, no esta lista**: `CA-66` escribe textual
 * `#/instancia/configuracion-del-chatbot/mensajes/boton-sin-respuesta`. No es una
 * derivación del rótulo como las tres que el docblock de `pantalla.tsx` declara
 * inventadas — acá hay fuente.
 *
 * ⛔⛔ **Sube ACÁ desde `pantalla.tsx` porque lo necesita un SEGUNDO consumidor**,
 * y ése es el motivo entero: `src/rutas.ts` —el marco— tiene que saber cuáles
 * existen para cumplir `CA-84` (`…/mensajes/xyz` **queda** en `…/mensajes`), y el
 * marco no puede importar la pantalla. La alternativa era escribir la lista dos
 * veces, que es la especie de defecto que el encabezado de `pantalla.tsx` ya
 * describe para los slugs: divergen y **nadie se pone rojo**.
 *
 * ⭐ `pantalla.tsx` no copia la lista: **tipa su tupla con este union**, así que
 * un slug nuevo escrito allá y no acá no compila.
 *
 * ⭐⭐ **RASTRO · `R3-UI2` · 28/9/2026 — EL OCTAVO.** `ADR-0082`: los dos
 * renglones de arriba se conservan enteros porque siguen valiendo para los siete
 * que ya estaban. `PRD_R3.md:1749-1757` suma la **octava y última** fila de
 * Mensajes del motor: «Encuesta de satisfacción», con dirección propia
 * `encuesta-de-satisfaccion` (`R3-F4-CA-04`, `PRD_R3.md:1849`, que escribe
 * textual `#/instancia/configuracion-del-chatbot/mensajes/encuesta-de-satisfaccion`).
 *
 * ⛔ **La octava es la ÚNICA que puede quedar vacía**, y eso NO se decide acá:
 * esta lista sólo dice qué direcciones existen. Lo opcional es el TIPO del texto
 * (`TextosOpcionalesDelChatbot`, `R3-CAA1` · B15) y la regla por fila vive en
 * `reglas.ts`. Ver `SLUG_DE_LA_ENCUESTA` abajo.
 *
 * ⛔ **El marco (`src/rutas.ts`) no se toca y no hace falta tocarlo**: importa
 * esta misma lista para cumplir `CA-84`, así que el octavo slug pasa a resolver
 * por estar acá. Una rama especial allá sería entrar en territorio de `R3-UI0`.
 */
export const MENSAJES_CON_DIRECCION = [
  "fuera-de-tema",
  "no-puedo-responder-eso",
  "sin-cobertura-en-el-banco",
  "respuesta-de-respaldo",
  "dame-un-segundo",
  "pedir-que-escriba",
  "boton-sin-respuesta",
  "encuesta-de-satisfaccion",
] as const

export type MensajeConDireccion = (typeof MENSAJES_CON_DIRECCION)[number]

/**
 * ⭐ `R3-UI2` · **El slug de la única fila OPCIONAL**, en un solo lugar.
 *
 * Lo comparan `pantalla.tsx` (para dibujar «—» y la ayuda de `CA-05` sólo en esa
 * fila) y `reglas.ts` (para habilitar «Guardar mensaje» con la regla de SU fila).
 * Escrito dos veces divergiría el día que alguien corrija una letra, y
 * **ninguno de los dos lados se pondría rojo**: los dos seguirían comparando
 * contra un literal que existe.
 *
 * ⚠️ Anotado con `MensajeConDireccion` a propósito: si el slug sale de la lista
 * de arriba, esta línea deja de compilar.
 */
export const SLUG_DE_LA_ENCUESTA: MensajeConDireccion = "encuesta-de-satisfaccion"

/** El rótulo visible de cada pestaña, textual de `CA-13` y de los bocetos. */
export const ROTULO_DE_LA_PESTANA: Record<Pestana, string> = {
  personalidad: "Personalidad",
  guardrails: "Guardrails",
  mensajes: "Mensajes del motor",
  derivacion: "Derivación",
  auditoria: "Auditoría",
}

/**
 * **El RÓTULO VISIBLE de la única acción de guardado, según lo que publica.**
 *
 * ⛔ Hay **una sola** acción de guardado en la pantalla, y es a propósito: `§5.2`
 * dice que guardar publica los NUEVE textos —*«no hay estado intermedio ni
 * circuito de revisión»*—, así que dos botones con alcances distintos sobre la
 * misma operación dejarían al operador sin saber cuál publica qué. Lo que cambia
 * con la pestaña activa es **qué dice el botón**.
 *
 * ⛔⛔ **RASTRO `[18/9/2026]`.** Esto era el **nombre accesible** y el rótulo
 * visible era «Guardar» fijo. El párrafo que lo justificaba decía, textual:
 * ~~*«Se conserva el global porque `pruebas/aw48-configuracion-del-chatbot.test.tsx`
 * lo fija por igualdad exacta (`botonPorTexto(contenedor, "Guardar")`), y porque
 * es el que ya está publicado»*~~ — y **las dos mitades de esa frase son el
 * defecto**: un examen no es dueño de un criterio, y «ya está publicado» describe
 * lo que hay que arreglar. `PRD_R1.1.md:666`-`:671` rotula las tres con estas
 * palabras, y los bocetos también. Se corrigió el producto **y** el examen.
 *
 * ⚠️ `guardrails` y `auditoria` dicen «Guardar» a secas, y eso NO es un default
 * perezoso: ninguna de las dos tiene un rótulo propio en el PRD —`:668` y `:673`
 * no nombran ningún botón de guardado para ellas—, así que inventarles uno sería
 * escribir un criterio que nadie escribió.
 */
export const NOMBRE_DEL_GUARDADO: Record<Pestana, string> = {
  personalidad: "Guardar personalidad",
  guardrails: "Guardar",
  mensajes: "Guardar mensaje",
  derivacion: "Guardar derivación",
  auditoria: "Guardar",
}

/**
 * **La base de la pantalla.** Tiene que coincidir con el prefijo que sirve el
 * marco (`SEGMENTOS.instancia` + `SEGMENTOS.configuracionDelChatbot`).
 *
 * ⚠️ Es la única línea de este directorio que se rompe si el marco renombra un
 * segmento, y por eso está en un solo lugar: el resto del módulo arma y desarma
 * direcciones con `urlDeLaPestana()` y `pestanaDelHash()`.
 */
export const BASE_DE_LA_PANTALLA = "#/instancia/configuracion-del-chatbot"

/** La dirección de una pestaña. `CA-14`…`CA-18`. */
export function urlDeLaPestana(pestana: Pestana): string {
  return `${BASE_DE_LA_PANTALLA}/${pestana}`
}

/** Los segmentos de la barra, ya normalizados: sin `#`, sin barras vacías. */
function segmentos(hash: string): readonly string[] {
  return hash
    .replace(/^#/, "")
    .split("/")
    .filter((segmento) => segmento !== "")
}

/** Desde dónde mirar: lo que viene después del segmento que nombra la pantalla. */
function colaDespuesDeLaPantalla(hash: string): readonly string[] {
  const partes = segmentos(hash)
  const i = partes.lastIndexOf("configuracion-del-chatbot")
  return i === -1 ? [] : partes.slice(i + 1)
}

/**
 * **Qué pestaña se lee en una dirección.** `CA-35`, `CA-81`, `CA-82` y `CA-83`.
 *
 * Cubre los cuatro casos que el PRD enumera con cuatro criterios distintos:
 *
 *   · `#/instancia/configuracion-del-chatbot` → `personalidad` (`CA-35`)
 *   · `#/configuracion-del-chatbot` → `personalidad` (`CA-81`)
 *   · `#/puro/configuracion-del-chatbot` → `personalidad` (`CA-82`)
 *   · `#/instancia/configuracion-del-chatbot/<desconocida>` → `personalidad` (`CA-83`)
 *
 * ⛔ La última **no es un `default` cómodo**: es la regla que el PRD escribió, y
 * por eso el desconocido cae en Personalidad y no en la primera pestaña que
 * exista. Si mañana se reordena la barra, esto no se mueve.
 */
export function pestanaDelHash(hash: string): Pestana {
  const [primera] = colaDespuesDeLaPantalla(hash)
  const conocida = PESTANAS.find((pestana) => pestana === primera)
  return conocida ?? "personalidad"
}

/**
 * La dirección del panel de alta de una categoría. `R1.1-F3-CA-45` (`:553`).
 *
 * ⭐ Es un valor y no una plantilla porque **no hay variantes**: el alta es una
 * sola. Editar una categoría existente NO tiene dirección propia —el PRD no se la
 * da— y por eso no hay acá una función que la arme.
 */
export const URL_NUEVA_CATEGORIA = `${urlDeLaPestana("guardrails")}/nueva`

/**
 * **Si la dirección deja abierto el panel de alta de categoría.** `CA-45` y
 * `R1.1-F5-CA-74` (`:893`).
 *
 * ⛔ Exige los DOS segmentos, `guardrails` y `nueva`. Con sólo el primero la
 * dirección es la lista, y abrir el panel ahí volvería a lo que había antes de
 * `CA-45`: un formulario siempre desplegado, o sea una dirección que no dice nada
 * porque describe las dos cosas.
 *
 * ⚠️ Un tercer segmento (`…/guardrails/nueva/loquesea`) NO abre el panel: se lee
 * como una dirección que esta pantalla no conoce, igual que hace `CA-84` con la
 * lista de mensajes.
 */
export function panelDeCategoriaDelHash(hash: string): boolean {
  const cola = colaDespuesDeLaPantalla(hash)
  return cola.length === 2 && cola[0] === "guardrails" && cola[1] === "nueva"
}

/**
 * **El mensaje del motor que la dirección deja abierto**, o `null` si la dirección
 * es la lista. `CA-72` y `CA-84`.
 *
 * ⛔ Devuelve el slug **tal como viene**, sin validarlo: quien lo compara es la
 * lista, que sabe cuáles existen. Un slug que no existe simplemente no abre
 * ninguna fila, y lo que queda es la lista — que es exactamente lo que `CA-84`
 * pide (`#/…/mensajes/xyz` queda en `#/…/mensajes`).
 */
export function mensajeAbiertoDelHash(hash: string): string | null {
  const cola = colaDespuesDeLaPantalla(hash)
  return cola[0] === "mensajes" ? (cola[1] ?? null) : null
}
